'use client';

import { useState, useEffect } from 'react';
import ConditionInput from './ConditionInput';
import CalculationResults from './CalculationResults';
import EducationalInfo from './EducationalInfo';



interface Condition {
  id: string;
  name: string;
  rating: number;
  maxRating: number;
  isBilateral: boolean;
  affectedSide?: 'left' | 'right' | 'both';
}

interface CalculationStep {
  stepNumber: number;
  conditionName: string;
  originalRating: number;
  bilateralBonus?: number;
  effectiveRating: number;
  remainingEfficiencyBefore: number;
  inefficiency: number;
  remainingEfficiencyAfter: number;
  description: string;
}

const STORAGE_KEY = 'va_calculator_conditions';

// VA 2027 compensation rates with dependents (monthly)
// Source: https://www.va.gov/disability/compensation-rates/veteran-rates/
// Updated with 2.8% COLA increase effective December 1, 2026
const compensationRates2026Base: { 
  [key: number]: { 
    alone: number; 
    withSpouse: number; 
    withSpouseAndOneParent: number;
    withSpouseAndTwoParents: number;
    withOneParent: number;
    withTwoParents: number;
    perChildUnder18: number;
    perChildOver18InSchool: number;
    spouseAidAttendance: number;
  } 
} = {
  10: { 
    alone: 180.43, 
    withSpouse: 180.43, 
    withSpouseAndOneParent: 180.43, 
    withSpouseAndTwoParents: 180.43, 
    withOneParent: 180.43, 
    withTwoParents: 180.43,
    perChildUnder18: 0,
    perChildOver18InSchool: 0,
    spouseAidAttendance: 0
  },
  20: { 
    alone: 356.68, 
    withSpouse: 356.68, 
    withSpouseAndOneParent: 356.68, 
    withSpouseAndTwoParents: 356.68, 
    withOneParent: 356.68, 
    withTwoParents: 356.68,
    perChildUnder18: 0,
    perChildOver18InSchool: 0,
    spouseAidAttendance: 0
  },
  30: { 
    alone: 552.47, 
    withSpouse: 617.80, 
    withSpouseAndOneParent: 672.59, 
    withSpouseAndTwoParents: 727.38, 
    withOneParent: 607.26, 
    withTwoParents: 662.05,
    perChildUnder18: 32.67,
    perChildOver18InSchool: 105.37,
    spouseAidAttendance: 65.33
  },
  40: { 
    alone: 795.84, 
    withSpouse: 883.30, 
    withSpouseAndOneParent: 950.74, 
    withSpouseAndTwoParents: 1018.17, 
    withOneParent: 863.27, 
    withTwoParents: 930.71,
    perChildUnder18: 43.21,
    perChildOver18InSchool: 140.19,
    spouseAidAttendance: 87.47
  },
  50: { 
    alone: 1132.90, 
    withSpouse: 1242.48, 
    withSpouseAndOneParent: 1324.05, 
    withSpouseAndTwoParents: 1405.62, 
    withOneParent: 1214.03, 
    withTwoParents: 1295.60,
    perChildUnder18: 54.79,
    perChildOver18InSchool: 175.97,
    spouseAidAttendance: 109.58
  },
  60: { 
    alone: 1435.06, 
    withSpouse: 1566.72, 
    withSpouseAndOneParent: 1663.01, 
    withSpouseAndTwoParents: 1759.30, 
    withOneParent: 1531.30, 
    withTwoParents: 1627.59,
    perChildUnder18: 65.33,
    perChildOver18InSchool: 210.74,
    spouseAidAttendance: 131.72
  },
  70: { 
    alone: 1808.25, 
    withSpouse: 1961.23, 
    withSpouseAndOneParent: 2071.01, 
    withSpouseAndTwoParents: 2180.80, 
    withOneParent: 1918.04, 
    withTwoParents: 2027.82,
    perChildUnder18: 76.93,
    perChildOver18InSchool: 246.56,
    spouseAidAttendance: 152.79
  },
  80: { 
    alone: 2102.15, 
    withSpouse: 2277.06, 
    withSpouseAndOneParent: 2400.34, 
    withSpouseAndTwoParents: 2523.62, 
    withOneParent: 2225.38, 
    withTwoParents: 2348.66,
    perChildUnder18: 87.47,
    perChildOver18InSchool: 281.34,
    spouseAidAttendance: 174.90
  },
  90: { 
    alone: 2362.26, 
    withSpouse: 2559.95, 
    withSpouseAndOneParent: 2696.73, 
    withSpouseAndTwoParents: 2833.51, 
    withOneParent: 2499.48, 
    withTwoParents: 2636.26,
    perChildUnder18: 98.00,
    perChildOver18InSchool: 316.11,
    spouseAidAttendance: 197.04
  },
  100: { 
    alone: 3938.58, 
    withSpouse: 4158.17, 
    withSpouseAndOneParent: 4332.92, 
    withSpouseAndTwoParents: 4507.68, 
    withOneParent: 4113.34, 
    withTwoParents: 4288.09,
    perChildUnder18: 109.58,
    perChildOver18InSchool: 351.94,
    spouseAidAttendance: 219.61
  }
};

type DependentStatus = 'alone' | 'withSpouse' | 'withSpouseAndOneParent' | 'withSpouseAndTwoParents' | 'withOneParent' | 'withTwoParents';

export default function CalculatorInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [combinedRating, setCombinedRating] = useState(0);
  const [monthlyCompensation, setMonthlyCompensation] = useState(0);
  const [dependentStatus, setDependentStatus] = useState<DependentStatus>('alone');
  const [childrenUnder18, setChildrenUnder18] = useState(0);
  const [childrenOver18InSchool, setChildrenOver18InSchool] = useState(0);
  const [calculationSteps, setCalculationSteps] = useState<CalculationStep[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    setIsHydrated(true);
    
    if (typeof window !== 'undefined') {
      try {
        const savedConditions = localStorage.getItem(STORAGE_KEY);
        if (savedConditions) {
          const parsed = JSON.parse(savedConditions);
          setConditions(parsed);
        }
      } catch (error) {
        console.error('Error loading saved conditions:', error);
      }
    }
  }, []);

  // Save to localStorage whenever conditions change
  useEffect(() => {
    if (isHydrated && typeof window !== 'undefined') {
      try {
        if (conditions.length > 0) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(conditions));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error saving conditions:', error);
      }
    }
  }, [conditions, isHydrated]);

  useEffect(() => {
    if (conditions.length > 0) {
      calculateCombinedRating();
    } else {
      setCombinedRating(0);
      setMonthlyCompensation(0);
    }
  }, [conditions, dependentStatus, childrenUnder18, childrenOver18InSchool]);

  const calculateCombinedRating = () => {
    if (conditions.length === 0) return;

    const steps: CalculationStep[] = [];
    
    // Sort conditions by rating (highest first)
    const sortedConditions = [...conditions].sort((a, b) => b.rating - a.rating);

    // Apply bilateral factor if applicable
    const processedConditions = sortedConditions.map(condition => {
      if (condition.isBilateral && condition.rating > 0) {
        // Add 10% bilateral factor according to VA policy
        const bilateralBonus = condition.rating * 0.1;
        return {
          ...condition,
          effectiveRating: Math.min(100, condition.rating + bilateralBonus),
          bilateralBonus
        };
      }
      return { ...condition, effectiveRating: condition.rating, bilateralBonus: 0 };
    });

    // Calculate combined rating using VA whole person efficiency formula
    // Each condition reduces remaining efficiency, not simply adds percentages
    let remainingEfficiency = 100;
    
    processedConditions.forEach((condition, index) => {
      const remainingEfficiencyBefore = remainingEfficiency;
      const inefficiency = (condition.effectiveRating / 100) * remainingEfficiency;
      remainingEfficiency -= inefficiency;
      
      let description = `${condition.name} at ${condition.rating}%`;
      if (condition.isBilateral && condition.bilateralBonus > 0) {
        description += ` receives +${condition.bilateralBonus.toFixed(1)}% bilateral bonus (total ${condition.effectiveRating.toFixed(1)}%)`;
      }
      description += ` reduces remaining efficiency from ${remainingEfficiencyBefore.toFixed(2)}% to ${remainingEfficiency.toFixed(2)}%`;
      
      steps.push({
        stepNumber: index + 1,
        conditionName: condition.name,
        originalRating: condition.rating,
        bilateralBonus: condition.isBilateral ? condition.bilateralBonus : undefined,
        effectiveRating: condition.effectiveRating,
        remainingEfficiencyBefore,
        inefficiency,
        remainingEfficiencyAfter: remainingEfficiency,
        description
      });
    });

    const combinedDisability = 100 - remainingEfficiency;
    
    // Round to nearest 10 per VA rounding rules
    // VA rounds 0-5 down, 6-9 up (e.g., 74% → 70%, 75% → 80%)
    const roundedRating = Math.round(combinedDisability / 10) * 10;
    
    // Ensure the rounded rating matches exactly with compensation table keys
    const validRatings = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const finalRating = validRatings.includes(roundedRating) ? roundedRating : 
                       validRatings.reduce((prev, curr) => 
                         Math.abs(curr - roundedRating) < Math.abs(prev - roundedRating) ? curr : prev
                       );
    
    setCombinedRating(finalRating);
    setCalculationSteps(steps);
    
    // Calculate total compensation with children using exact compensation table
    const rateTable = compensationRates2026Base[finalRating];
    if (!rateTable) {
      setMonthlyCompensation(0);
      return;
    }

    let baseCompensation = rateTable[dependentStatus];
    
    // Add child compensation (only for ratings 30% and above per VA policy)
    if (finalRating >= 30) {
      const childUnder18Compensation = childrenUnder18 * rateTable.perChildUnder18;
      const childOver18Compensation = childrenOver18InSchool * rateTable.perChildOver18InSchool;
      baseCompensation += childUnder18Compensation + childOver18Compensation;
    }
    
    setMonthlyCompensation(baseCompensation);
  };

  const handleAddCondition = (condition: Omit<Condition, 'id'>) => {
    setConditions([...conditions, { ...condition, id: Date.now().toString() }]);
  };

  const handleRemoveCondition = (id: string) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const handleUpdateCondition = (id: string, updates: Partial<Condition>) => {
    setConditions(conditions.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  };

  const handlePrint = () => {
    if (!isHydrated) return;
    window.print();
  };

  const handleReset = () => {
    if (!isHydrated) return;
    if (window.confirm('Are you sure you want to reset the calculator? This will remove all conditions and clear saved data.')) {
      setConditions([]);
      setCombinedRating(0);
      setMonthlyCompensation(0);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:332:6" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="332" data-component-end-line="340" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:333:8" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="333" data-component-end-line="339" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:334:10" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="334" data-component-end-line="338" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
            <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:335:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="335" data-component-end-line="335" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-8 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:336:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="336" data-component-end-line="336" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded%22%7D" className="h-64 bg-muted rounded"></div>
            <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:337:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="337" data-component-end-line="337" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" className="h-96 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:345:4" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="345" data-component-end-line="399" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:346:6" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="346" data-component-end-line="398" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:347:8" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="347" data-component-end-line="392" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Input */}
          <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:349:10" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="349" data-component-end-line="373" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            <ConditionInput data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:350:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="350" data-component-end-line="355" data-component-file="CalculatorInteractive.tsx" data-component-name="ConditionInput" data-component-content="%7B%22elementName%22%3A%22ConditionInput%22%7D"
              conditions={conditions}
              onAddCondition={handleAddCondition}
              onRemoveCondition={handleRemoveCondition}
              onUpdateCondition={handleUpdateCondition}
            />
            
            <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:357:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="357" data-component-end-line="372" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%22%7D" className="lg:hidden">
              <CalculationResults data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:358:14" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="358" data-component-end-line="371" data-component-file="CalculatorInteractive.tsx" data-component-name="CalculationResults" data-component-content="%7B%22elementName%22%3A%22CalculationResults%22%7D"
                conditions={conditions}
                combinedRating={combinedRating}
                monthlyCompensation={monthlyCompensation}
                dependentStatus={dependentStatus}
                childrenUnder18={childrenUnder18}
                childrenOver18InSchool={childrenOver18InSchool}
                calculationSteps={calculationSteps}
                onDependentStatusChange={setDependentStatus}
                onChildrenUnder18Change={setChildrenUnder18}
                onChildrenOver18Change={setChildrenOver18InSchool}
                onPrint={handlePrint}
                onReset={handleReset}
              />
            </div>
          </div>

          {/* Right Column - Results */}
          <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:376:10" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="376" data-component-end-line="391" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Ablock%20sticky%20top-24%20self-start%22%7D" className="hidden lg:block sticky top-24 self-start">
            <CalculationResults data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:377:12" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="377" data-component-end-line="390" data-component-file="CalculatorInteractive.tsx" data-component-name="CalculationResults" data-component-content="%7B%22elementName%22%3A%22CalculationResults%22%7D"
              conditions={conditions}
              combinedRating={combinedRating}
              monthlyCompensation={monthlyCompensation}
              dependentStatus={dependentStatus}
              childrenUnder18={childrenUnder18}
              childrenOver18InSchool={childrenOver18InSchool}
              calculationSteps={calculationSteps}
              onDependentStatusChange={setDependentStatus}
              onChildrenUnder18Change={setChildrenUnder18}
              onChildrenOver18Change={setChildrenOver18InSchool}
              onPrint={handlePrint}
              onReset={handleReset}
            />
          </div>
        </div>

        {/* Educational Information */}
        <div data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:395:8" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="395" data-component-end-line="397" data-component-file="CalculatorInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%22%7D" className="mt-12">
          <EducationalInfo data-component-id="src/app/benefits-calculator/components/CalculatorInteractive.tsx:396:10" data-component-path="src/app/benefits-calculator/components/CalculatorInteractive.tsx" data-component-line="396" data-component-end-line="396" data-component-file="CalculatorInteractive.tsx" data-component-name="EducationalInfo" data-component-content="%7B%22elementName%22%3A%22EducationalInfo%22%7D" />
        </div>
      </div>
    </div>
  );
}